import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash, UploadSimple } from "@phosphor-icons/react";

export default function CapturesSection({ date, warehouse, closed, kind = "osd", title, emptyText, embedded = false }) {
  const [captures, setCaptures] = useState([]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setCaptures(await api.getCaptures(date, warehouse, kind));
    } catch {
      toast.error("Failed to load OS&D captures");
    }
  };

  useEffect(() => {
    load();
  }, [date, warehouse, kind]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", note);
      fd.append("date", date);
      fd.append("warehouse", warehouse);
      fd.append("kind", kind);
      await api.uploadCapture(fd);
      toast.success("Capture uploaded");
      setNote("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteCapture(id);
      toast.success("Capture deleted");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Delete failed");
    }
  };

  const body = (
    <>
      {!closed && (
        <div className={embedded ? "grid grid-cols-1 md:grid-cols-12 gap-3" : "p-4 border-b border-slate-100 grid grid-cols-1 md:grid-cols-12 gap-3"}>
          <div className="md:col-span-4">
            <input
              ref={fileRef}
              data-testid={`${kind}-capture-file-input`}
              type="file"
              accept="image/*,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-50"
            />
          </div>
          <div className="md:col-span-6">
            <input
              data-testid={`${kind}-capture-note-input`}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (e.g. crushed corner, shortage of 2 boxes)"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
            />
          </div>
          <div className="md:col-span-2">
            <button
              data-testid={`${kind}-capture-upload-btn`}
              onClick={upload}
              disabled={!file || uploading}
              className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md font-semibold px-4 py-2 text-sm transition-colors duration-200"
            >
              <UploadSimple size={16} weight="bold" />
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      )}

      <div className={embedded ? "mt-4" : "p-4"}>
        {captures.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4" data-testid={`${kind}-captures-empty`}>
            {emptyText || "No OS&D captures yet — upload a photo or video with a note when something arrives damaged or short."}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {captures.map((c) => (
              <figure
                key={c.id}
                className="border border-slate-200 rounded-md overflow-hidden bg-white"
                data-testid={`capture-card-${c.id}`}
              >
                {(c.content_type || "").startsWith("video/") ? (
                  <video
                    src={api.captureImageUrl(c.id)}
                    controls
                    className="w-full h-36 object-cover bg-slate-900"
                    data-testid={`capture-video-${c.id}`}
                  />
                ) : (
                  <a href={api.captureImageUrl(c.id)} target="_blank" rel="noopener noreferrer">
                    <img
                      src={api.captureImageUrl(c.id)}
                      alt={c.note || "OS&D capture"}
                      className="w-full h-36 object-cover bg-slate-100"
                      data-testid={`capture-image-${c.id}`}
                    />
                  </a>
                )}
                <figcaption className="p-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 break-words">{c.note || "(no note)"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      by {c.uploaded_by} ·{" "}
                      {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  {!closed && (
                    <button
                      data-testid={`capture-delete-${c.id}`}
                      onClick={() => remove(c.id)}
                      className="text-red-500 hover:text-red-600 p-1 flex-shrink-0"
                      title="Delete capture"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div data-testid={`${kind}-captures-section`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Camera size={16} weight="duotone" /> {title || "OS&D Captures — damage / dispute evidence"}
          </div>
          <div className="text-sm font-semibold text-slate-700" data-testid={`${kind}-captures-count`}>
            {captures.length} {captures.length === 1 ? "capture" : "captures"}
          </div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid={`${kind}-captures-section`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          <Camera size={16} weight="duotone" /> {title || "OS&D Captures — damage / dispute evidence"}
        </div>
        <div className="text-sm font-semibold text-slate-700" data-testid={`${kind}-captures-count`}>
          {captures.length} {captures.length === 1 ? "capture" : "captures"}
        </div>
      </div>
      {body}
    </div>
  );
}
